import type { FC } from 'react'
import { useCallback } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, IconButton, Tooltip } from '@mui/material'
import { ChevronLeftOutlined, PushPinOutlined } from '@mui/icons-material'
import { standardTooltipProps } from '@/common/components/tooltipStyles'

interface SidebarHeaderProps {
  readonly isPinned: boolean
  readonly onCollapse: () => void
  readonly onTogglePin: () => void
}

export const SidebarHeader: FC<SidebarHeaderProps> = ({ isPinned, onCollapse, onTogglePin }) => {
  const handleCollapseClick = useCallback(() => {
    onCollapse()
  }, [onCollapse])

  const handlePinClick = useCallback(() => {
    onTogglePin()
  }, [onTogglePin])

  return (
    <Box sx={headerContainerSx}>
      <Tooltip {...standardTooltipProps} title="Collapse sidebar" placement="right">
        <IconButton
          onClick={handleCollapseClick}
          size="small"
          sx={toggleButtonSx}
          aria-label="Collapse sidebar"
        >
          <ChevronLeftOutlined sx={iconSx} />
        </IconButton>
      </Tooltip>

      <Tooltip
        {...standardTooltipProps}
        title={isPinned ? 'Unpin sidebar (auto-hide)' : 'Pin sidebar'}
        placement="right"
      >
        <IconButton
          onClick={handlePinClick}
          size="small"
          sx={pinButtonSx(isPinned)}
          aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
        >
          <PushPinOutlined sx={pinIconSx(isPinned)} />
        </IconButton>
      </Tooltip>
    </Box>
  )
}

const headerContainerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1,
  py: 1.5,
  borderBottom: 1,
  borderColor: 'divider',
}

const toggleButtonSx: SxProps<Theme> = {
  color: 'text.secondary',
  transition: 'color 200ms ease-in-out',
  '&:hover': {
    color: 'text.primary',
    backgroundColor: 'transparent',
  },
}

const iconSx: SxProps<Theme> = {
  fontSize: 20,
}

const pinButtonSx = (isPinned: boolean): SxProps<Theme> => ({
  color: isPinned ? 'primary.main' : 'text.secondary',
  transform: isPinned ? 'rotate(0deg)' : 'rotate(45deg)',
  transition: 'transform 200ms ease-in-out, color 200ms ease-in-out',
  '&:hover': {
    color: 'text.primary',
    backgroundColor: 'transparent',
  },
})

const pinIconSx = (isPinned: boolean): SxProps<Theme> => ({
  fontSize: 18,
  opacity: isPinned ? 1 : 0.7,
})
